import { useState, useCallback, type DragEvent } from "react";

type DragStateHandlers<T extends HTMLElement = HTMLElement> = {
  handleDragStart: (event: DragEvent<T>) => void;
  handleDragEnd: (event: DragEvent<T>) => void;
  handleDragOver: (event: DragEvent<T>) => void;
  handleDragEnter: (event: DragEvent<T>) => void;
  handleDragLeave: (event: DragEvent<T>) => void;
  handleDrop: (event: DragEvent<T>) => void;
};

type UseDragStateOptions<T extends HTMLElement = HTMLElement> = {
  onDragStart?: (event: DragEvent<T>) => void;
  onDragEnd?: (event: DragEvent<T>) => void;
  onDragOver?: (event: DragEvent<T>) => void;
  onDragEnter?: (event: DragEvent<T>) => void;
  onDragLeave?: (event: DragEvent<T>) => void;
  onDrop?: (event: DragEvent<T>) => void;
};

type UseDragStateReturn<T extends HTMLElement = HTMLElement> = {
  isDragging: boolean;
  isDragOver: boolean;
  showDropHighlight: boolean;
  handlers: DragStateHandlers<T>;
};

export const useDragState = <T extends HTMLElement = HTMLElement>(
  options: UseDragStateOptions<T> = {},
): UseDragStateReturn<T> => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const { onDragStart, onDragEnd, onDragOver, onDragEnter, onDragLeave, onDrop } = options;

  const handleDragStart = useCallback(
    (event: DragEvent<T>): void => {
      setIsDragging(true);
      onDragStart?.(event);
    },
    [onDragStart],
  );

  const handleDragEnd = useCallback(
    (event: DragEvent<T>): void => {
      setIsDragging(false);
      onDragEnd?.(event);
    },
    [onDragEnd],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<T>): void => {
      event.preventDefault();
      setIsDragOver(true);
      onDragOver?.(event);
    },
    [onDragOver],
  );

  const handleDragEnter = useCallback(
    (event: DragEvent<T>): void => {
      event.preventDefault();
      setIsDragOver(true);
      onDragEnter?.(event);
    },
    [onDragEnter],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<T>): void => {
      const relatedTarget = event.relatedTarget as Node | null;
      const currentTarget = event.currentTarget as Node;
      if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
        setIsDragOver(false);
      }
      onDragLeave?.(event);
    },
    [onDragLeave],
  );

  const handleDrop = useCallback(
    (event: DragEvent<T>): void => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      onDrop?.(event);
    },
    [onDrop],
  );

  // Don't show highlight when dragging over self
  const showDropHighlight = isDragOver && !isDragging;

  return {
    isDragging,
    isDragOver,
    showDropHighlight,
    handlers: {
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
    },
  };
};
